import React from 'react';
import { Drawer, IconButton, Typography, Box, Button, useMediaQuery, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import theme from 'theme';
import HLButton from 'components/HLButton';

export interface ActionButtonProps {
  label: string;
  ButtonProps?: React.ComponentProps<typeof Button> & { isLoading?: boolean };
}

export interface CustomDrawerProps {
  open: boolean;
  onClose: (event: React.KeyboardEvent | React.MouseEvent) => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: number | string;
  primaryAction?: ActionButtonProps;
  secondaryAction?: ActionButtonProps;
  sx?: React.CSSProperties;
  footerItems?: React.ReactNode;
}

const MAX_WIDTH = 528;

function HLDrawer({
  open,
  onClose,
  title,
  children,
  maxWidth = MAX_WIDTH,
  primaryAction,
  secondaryAction,
  sx,
  footerItems,
}: CustomDrawerProps) {
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          ...sx,
          maxWidth: isMobile ? '100%' : maxWidth,
          width: '100%',
          height: isMobile ? '90vh' : '100vh',
          borderRadius: isMobile ? '16px 16px 0 0' : '0',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 2.5,
          borderBottom: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          background: '#fff',
          zIndex: 2,
        }}
      >
        <Typography variant='body1' fontSize={24} fontWeight={400} lineHeight='100%'>
          {title}
        </Typography>
        <IconButton onClick={onClose} size='small' sx={{ p: 0 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2.5,
          position: 'relative',
          background: theme.custom.backgroundColor,
        }}
      >
        {children}
      </Box>

      {/* Footer (Mobile only) */}
      {((isMobile && (primaryAction || secondaryAction)) || footerItems) && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #E0E0E0',
            background: '#fff',
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            display: 'flex',
            gap: 2,
            flexDirection: 'column',
          }}
        >
          {primaryAction && (
            <HLButton variant='contained' fullWidth {...primaryAction.ButtonProps}>
              {primaryAction.label}
            </HLButton>
          )}
          {secondaryAction && (
            <HLButton variant='outlined' fullWidth {...secondaryAction.ButtonProps}>
              {secondaryAction.label}
            </HLButton>
          )}
          {footerItems && footerItems}
        </Box>
      )}
    </Drawer>
  );
}

export default HLDrawer;
